/**
 * Hook para listar gift cards con categorías y búsqueda.
 */
import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import useSearchParam from "ui/common/hooks/useSearchParam";
import { MainRoutes } from "ui/common/router/MainRouter.types";
import { giftcardsApi } from "../api/giftcardsApi";
import type { GiftCard, SearchTerm } from "ui/giftcards/types/giftcard.types";
import type { GiftCardCatalogDto } from "common/models";

function mapCatalogToGiftCard(dto: GiftCardCatalogDto): GiftCard {
    return {
        catalog_id: dto.catalog_id,
        empresa: dto.empresa,
        giftcard: dto.giftcard,
        imagenGiftcardPNG: dto.imagenGiftcardPNG,
        imagenGiftcardJPG: dto.imagenGiftcardJPG,
        logoWallet: dto.logoWallet,
        categoria: dto.categoria,
        activo: dto.activo,
        isSpecial: typeof dto.isSpecial === "boolean" ? dto.isSpecial : Boolean(dto.isSpecial && typeof dto.isSpecial === "object" && Object.keys(dto.isSpecial).length > 0),
        sourceType: dto.sourceType,
    };
}

export function useFetchGiftCards() {
    const [categoryParam, setCategoryParam] = useSearchParam("category");
    const [searchParam] = useSearchParam("query");
    const { data: response, isLoading } = useQuery(
        MainRoutes.GIFT_CARDS,
        () => giftcardsApi.getAllCards()
    );
    const [data, setData] = useState<GiftCard[]>([]);

    const getCategories = (giftcards: GiftCard[]) => {
        const rawData = giftcards.map((item) => item.categoria);
        return Array.from(new Set(rawData));
    };

    const getGiftCardsData = (searchTerm?: SearchTerm) => {
        if (!searchTerm || searchTerm.value.length === 0) return data;
        if (searchTerm.key === "giftcard") {
            return data.filter((item: GiftCard) =>
                `${item.giftcard} ${item.empresa}`.toLowerCase().includes(searchTerm.value.toLowerCase())
            );
        }
        return data.filter((item: GiftCard) => item[searchTerm.key] === searchTerm.value);
    };

    const groupedByCategory = (items: GiftCard[]) =>
        items.reduce<Record<string, GiftCard[]>>((acc, item) => {
            if (!acc[item.categoria]) acc[item.categoria] = [];
            acc[item.categoria].push(item);
            return acc;
        }, {});

    const [giftcards, setGiftCards] = useState<Record<string, GiftCard[]>>({});
    const categories: string[] = getCategories(data);
    const categorySelected = categoryParam ?? "";

    const handleSelectCategory = (category: string) => {
        setCategoryParam(category === categorySelected ? "" : category);
    };

    useEffect(() => {
        let filteredData = data;
        if (categorySelected) {
            filteredData = getGiftCardsData({ key: "categoria", value: categorySelected });
        }
        if (searchParam) {
            filteredData = getGiftCardsData({ key: "giftcard", value: searchParam });
        }
        setGiftCards(groupedByCategory(filteredData));
    }, [categorySelected, searchParam, data]);

    useEffect(() => {
        if (response) {
            setData(response.map(mapCatalogToGiftCard));
        }
    }, [response]);

    return {
        categories,
        categorySelected,
        handleSelectCategory,
        giftcards,
        isLoading,
        response,
    };
}
